/**
 * api/agents.js — queries para o Catálogo de Agents.
 *
 * Endpoints (Onda 3):
 *   GET /agents                     — lista com filtros e paginação
 *     query: category?, search?, permission_level?, limit?, offset?
 *   GET /agents/{id}                — detalhe de um agente
 *
 * Total do catálogo: 394 sub-agents auditados.
 *
 * Agentes especialistas (A1): o AgentOut ganhou o estado do prompt de especialista.
 * Os endpoints de prompt (/agents/{nome}/prompt…) ganham hooks com as telas da A2.
 *
 * Convenções:
 *   - agentKeys seguem hierarquia: ['agents', filtros?, id?]
 *   - staleTime = 5 min (catálogo muda raramente em produção).
 *   - ApiError 403 → ForbiddenBanner.
 *   - Clean: sem lógica de UI.
 */
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

/**
 * @typedef {'especialista'|'incompleto'|'sem_prompt'} PromptStatus
 * @typedef {'leitura'|'edicao'|'edicao+shell'|'revisao'} ToolProfile
 * @typedef {{
 *   id: string, name: string, category: string, description: string,
 *   system_prompt: string|null, permission_level: string, default_model_hint: string|null,
 *   skills: string[], is_active: boolean, in_use: boolean, used_in_steps: string[],
 *   prompt_status: PromptStatus, prompt_chars: number, tool_profile: ToolProfile,
 *   status: 'ativo'|'rascunho'
 * }} AgentOut
 */

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const agentKeys = {
  all:      ()                 => ['agents'],
  list:     (filters)          => ['agents', 'list', filters],
  detail:   (id)               => ['agents', id],
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * useAgents — lista paginada de agentes com filtros.
 *
 * @param {{
 *   category?: string,
 *   search?: string,
 *   permission_level?: string,
 *   limit?: number,
 *   offset?: number,
 *   prompt_status?: PromptStatus
 * }} filters
 */
export function useAgents(filters = {}) {
  const api = useApi();

  const params = new URLSearchParams();
  if (filters.category)          params.set('category',         filters.category);
  if (filters.search)            params.set('search',           filters.search);
  if (filters.permission_level)  params.set('permission_level', filters.permission_level);
  if (filters.limit != null)     params.set('limit',            String(filters.limit));
  if (filters.offset != null)    params.set('offset',           String(filters.offset));
  if (filters.in_use)            params.set('in_use',           'true');
  if (filters.prompt_status)     params.set('prompt_status',    filters.prompt_status);
  const qs = params.toString();

  return useQuery({
    queryKey: agentKeys.list(filters),
    queryFn:  () => api.get(`/agents${qs ? `?${qs}` : ''}`),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Teto do `limit` do GET /agents (o router aceita 1..200). */
export const LIMITE_DA_PAGINA_DE_AGENTES = 200;

/**
 * useTodosOsAgentes — o catálogo INTEIRO (com `search` opcional), paginando o GET /agents
 * de 200 em 200 até o `total`. Para listas de escolha: nenhum agente fica de fora por
 * causa do teto da página (o catálogo tem ~400 agentes).
 *
 * @param {{ search?: string }} filters
 * @returns {import('@tanstack/react-query').UseQueryResult<{items: AgentOut[], total: number}>}
 */
export function useTodosOsAgentes(filters = {}) {
  const api = useApi();
  const search = filters.search || undefined;
  return useQuery({
    queryKey: ['agents', 'todos', { search }],
    queryFn: async () => {
      const itens = [];
      let total = null;
      for (let offset = 0; ; offset += LIMITE_DA_PAGINA_DE_AGENTES) {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        params.set('limit', String(LIMITE_DA_PAGINA_DE_AGENTES));
        params.set('offset', String(offset));
        const res = await api.get(`/agents?${params.toString()}`);
        const pagina = Array.isArray(res) ? res : (res?.items ?? []);
        if (total == null && Number.isFinite(Number(res?.total))) total = Number(res.total);
        itens.push(...pagina);
        // Fim: página vazia, página incompleta ou o total já alcançado.
        if (!pagina.length || pagina.length < LIMITE_DA_PAGINA_DE_AGENTES || (total != null && itens.length >= total)) break;
      }
      return { items: itens, total: total ?? itens.length };
    },
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * useAgent — detalhe de um único agente.
 * @param {string|null} id
 */
export function useAgent(id) {
  const api = useApi();
  return useQuery({
    queryKey: agentKeys.detail(id),
    queryFn:  () => api.get(`/agents/${id}`),
    enabled:  Boolean(id),
    staleTime: 5 * 60_000,
  });
}

/**
 * useAgentsActivity — atividade GERAL dos agentes (Sala dos Agentes).
 *
 * Visão global/agregada, não presa a um projeto: o último evento de cada agente
 * across execuções do tenant. GET /agents/activity.
 *
 * @param {{ windowHours?: number, activeOnly?: boolean, refetchInterval?: number }} [opts]
 */
export function useAgentsActivity({ windowHours = 24, activeOnly = false, refetchInterval, enabled = true } = {}) {
  const api = useApi();

  const params = new URLSearchParams();
  params.set('window_hours', String(windowHours));
  if (activeOnly) params.set('active_only', 'true');
  const qs = params.toString();

  return useQuery({
    queryKey: ['agents', 'activity', windowHours, activeOnly],
    queryFn:  () => api.get(`/agents/activity?${qs}`),
    staleTime: 3_000,
    enabled,
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

/**
 * useJourneyPresence — heartbeat de presença enquanto o usuário está na
 * CONFIGURAÇÃO (jornada) de um projeto. Bate POST /agents/presence a cada
 * `intervalMs`, o que (re)publica pings de atividade do squad no Redis — assim a
 * Sala dos Agentes mostra o squad ATIVO durante toda a configuração, não só nos
 * segundos após um passo de IA. Best-effort: falhas são ignoradas.
 *
 * @param {string|null|undefined} projectId
 * @param {{ intervalMs?: number }} [opts]
 */
export function useJourneyPresence(projectId, { intervalMs = 60_000 } = {}) {
  const api = useApi();
  useEffect(() => {
    if (!projectId) return undefined;
    let stopped = false;
    const ping = () => {
      api.post('/agents/presence', { project_id: projectId }).catch(() => {});
    };
    ping(); // imediato ao entrar na configuração
    const id = setInterval(() => { if (!stopped) ping(); }, intervalMs);
    return () => { stopped = true; clearInterval(id); };
  }, [projectId, intervalMs, api]);
}

/**
 * useSuggestAgents — sugere agentes do catálogo para um escopo + tipo de entrega.
 *
 * POST /agents/suggest. Body: { delivery_type, project_id?, scope_text?, max_agents? }.
 * Advisory (não altera o pipeline). Retorna { delivery_type, scope_summary, notes, suggested[] }.
 */
export function useSuggestAgents() {
  const api = useApi();
  return useMutation({
    mutationKey: ['ai'],  // dispara o BlockingOverlay global de "IA trabalhando"
    meta: { aiLabel: 'A IA está montando o squad…', aiSublabel: 'Escolhendo os melhores agentes do catálogo para o escopo e o tipo de entrega.' },
    mutationFn: (body) => api.post('/agents/suggest', body),
  });
}

/**
 * useProjectSteps — squad aplicado a um projeto (qual agente faz cada passo).
 * GET /agents/project/{id}/steps → { project_id, steps:[{step,label,default_agent,agent,overridden}] }.
 */
export function useProjectSteps(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['agents', 'project-steps', projectId],
    queryFn:  () => api.get(`/agents/project/${projectId}/steps`),
    enabled:  Boolean(projectId),
    staleTime: 10_000,
  });
}

/**
 * useSetProjectSteps — aplica um squad a um projeto (override step→agente).
 * PUT /agents/project/{id}/steps  body { step_agents: { step: agente } }.
 */
export function useSetProjectSteps() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, step_agents }) =>
      api.put(`/agents/project/${projectId}/steps`, { step_agents }),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['agents', 'project-steps', vars.projectId] }),
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * useUpdateAgent — edita um agente do catálogo (override por tenant).
 *
 * PUT /agents/{id} com os campos editáveis. Ao ter sucesso invalida a chave
 * raiz ['agents'] para que lista e detalhe recarreguem com os novos dados.
 *
 * @example
 *   const update = useUpdateAgent();
 *   update.mutate({ id, body: { model: 'opus', is_active: false } });
 */
export function useUpdateAgent() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    // body pode conter: description, model, permission_level, category,
    // is_active e system_prompt (texto longo).
    mutationFn: ({ id, body }) => api.put(`/agents/${id}`, body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: agentKeys.all() }),
  });
}

/**
 * useCreateAgent — cria um novo agente.
 *
 * POST /agents body { name, description, model, permission_level, category,
 * system_prompt? } → 201 AgentOut. Ao ter sucesso invalida a chave raiz.
 *
 * @example
 *   const create = useCreateAgent();
 *   create.mutate({ name, description, model, permission_level, category });
 */
export function useCreateAgent() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/agents', body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: agentKeys.all() }),
  });
}

/**
 * useAssignSkill — atribui uma skill a um agente.
 *
 * POST /agents/{agentName}/skills body { skill_name }. Invalida lista e detalhe.
 *
 * @example
 *   const assign = useAssignSkill();
 *   assign.mutate({ agentName, skillName });
 */
export function useAssignSkill() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ agentName, skillName }) =>
      api.post(`/agents/${agentName}/skills`, { skill_name: skillName }),
    onSuccess: (_data, { agentName }) => {
      qc.invalidateQueries({ queryKey: agentKeys.all() });
      qc.invalidateQueries({ queryKey: agentKeys.detail(agentName) });
    },
  });
}

/**
 * useUnassignSkill — remove uma skill de um agente.
 *
 * DELETE /agents/{agentName}/skills/{skillName}. Invalida lista e detalhe.
 *
 * @example
 *   const unassign = useUnassignSkill();
 *   unassign.mutate({ agentName, skillName });
 */
export function useUnassignSkill() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ agentName, skillName }) =>
      api.delete(`/agents/${agentName}/skills/${skillName}`),
    onSuccess: (_data, { agentName }) => {
      qc.invalidateQueries({ queryKey: agentKeys.all() });
      qc.invalidateQueries({ queryKey: agentKeys.detail(agentName) });
    },
  });
}

// ─── Prompt de especialista (A1 · telas da A2) ────────────────────────────────

/**
 * Chaves do prompt de especialista. Ficam fora de ['agents'] de propósito: o prompt
 * muda por projeto e não deve recarregar a lista inteira do catálogo a cada edição.
 */
export const promptKeys = {
  all:      ()                        => ['agent-prompt'],
  efetivo:  (nome, projectId)         => ['agent-prompt', 'efetivo', nome, projectId ?? null],
  versoes:  (nome, scope, projectId)  => ['agent-prompt', 'versoes', nome, scope ?? null, projectId ?? null],
  versao:   (nome, versionId)         => ['agent-prompt', 'versao', nome, versionId],
  skills:   (nome)                    => ['agent-prompt', 'skills', nome],
};

/** Chaves da memória do agente (A6). */
export const memoriaKeys = {
  all:     ()                              => ['agent-memoria'],
  memoria: (nome, projectId, historico)    => ['agent-memoria', nome, projectId ?? null, Boolean(historico)],
};

const _nome = (nome) => encodeURIComponent(String(nome || ''));

/**
 * usePromptEfetivo — o prompt que o agente usa (projeto › tenant › catálogo) e a validação.
 * GET /agents/{nome}/prompt?project_id= → PromptEfetivoOut (SPEC A1 §6).
 */
export function usePromptEfetivo(nome, projectId, { enabled = true } = {}) {
  const api = useApi();
  const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  return useQuery({
    queryKey: promptKeys.efetivo(nome, projectId),
    queryFn:  () => api.get(`/agents/${_nome(nome)}/prompt${qs}`),
    enabled:  Boolean(nome) && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

/**
 * usePromptVersoes — histórico de versões (mais nova primeiro).
 * GET /agents/{nome}/prompt/versions?scope=&project_id= → { items: VersaoResumo[] }.
 */
export function usePromptVersoes(nome, { scope, projectId, enabled = true } = {}) {
  const api = useApi();
  const params = new URLSearchParams();
  if (scope)     params.set('scope', scope);
  if (projectId) params.set('project_id', projectId);
  const qs = params.toString();
  return useQuery({
    queryKey: promptKeys.versoes(nome, scope, projectId),
    queryFn:  () => api.get(`/agents/${_nome(nome)}/prompt/versions${qs ? `?${qs}` : ''}`),
    enabled:  Boolean(nome) && enabled,
    staleTime: 10_000,
    retry: false,
  });
}

/**
 * usePromptVersao — texto de uma versão e o diff contra a anterior do mesmo escopo.
 * GET /agents/{nome}/prompt/versions/{id} → VersaoResumo & { texto, diff_anterior }.
 */
export function usePromptVersao(nome, versionId) {
  const api = useApi();
  return useQuery({
    queryKey: promptKeys.versao(nome, versionId),
    queryFn:  () => api.get(`/agents/${_nome(nome)}/prompt/versions/${encodeURIComponent(versionId)}`),
    enabled:  Boolean(nome && versionId),
    staleTime: 60_000,
    retry: false,
  });
}

/** Invalida tudo que depende do prompt de um agente (selo da conferência inclusive). */
function _invalidarPrompt(qc, projectId) {
  qc.invalidateQueries({ queryKey: promptKeys.all() });
  qc.invalidateQueries({ queryKey: agentKeys.all() });
  if (projectId) qc.invalidateQueries({ queryKey: ['squad-conferencia', projectId] });
  else qc.invalidateQueries({ queryKey: ['squad-conferencia'] });
}

/**
 * useSalvarPrompt — grava uma nova versão do prompt.
 * POST /agents/{nome}/prompt body { texto, scope, project_id?, origem }.
 * 201 nova versão · 200 sem mudança · 422 segredo (`segredos:[{tipo,linha}]`) · 403.
 */
export function useSalvarPrompt() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ nome, texto, scope = 'project', projectId, origem = 'manual' }) =>
      api.post(`/agents/${_nome(nome)}/prompt`, {
        texto, scope, origem, ...(projectId ? { project_id: projectId } : {}),
      }),
    onSuccess: (_d, vars) => _invalidarPrompt(qc, vars.projectId),
  });
}

/**
 * usePromoverPrompt — promove uma versão do projeto ao tenant.
 * POST /agents/{nome}/prompt/versions/{id}/promote (exige agent_catalog:create_edit).
 */
export function usePromoverPrompt() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ nome, versionId }) =>
      api.post(`/agents/${_nome(nome)}/prompt/versions/${encodeURIComponent(versionId)}/promote`, {}),
    onSuccess: (_d, vars) => _invalidarPrompt(qc, vars.projectId),
  });
}

/**
 * usePropostaPrompt — o AI Core propõe um prompt de especialista (NÃO salva).
 * POST /agents/{nome}/prompt/proposal body { project_id? }. 502 traz `detail` em pt-BR.
 * Sem mutationKey ['ai'] de propósito: o editor segue livre durante a chamada (FR-13).
 */
export function usePropostaPrompt() {
  const api = useApi();
  return useMutation({
    mutationFn: ({ nome, projectId }) =>
      api.post(`/agents/${_nome(nome)}/prompt/proposal`, projectId ? { project_id: projectId } : {}),
  });
}

/**
 * useSkillsDoAgente — skills vinculadas ao agente no catálogo, com `tem_conteudo`.
 * GET /agents/{nome}/skills → { items: [{ name, origem, description, tem_conteudo, arquivos }] }.
 */
export function useSkillsDoAgente(nome, { enabled = true } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: promptKeys.skills(nome),
    queryFn:  () => api.get(`/agents/${_nome(nome)}/skills`),
    enabled:  Boolean(nome) && enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * useMemoriaDoAgente — o que o agente já aprendeu no projeto (SPEC A6 FR-13, §6).
 * GET /agents/{nome}/memoria?project_id=&historico=0|1 → MemoriaDoAgenteOut
 * { licoes, licoes_outros_projetos, episodios, omitidos }. Só leitura.
 * 403 sem permissão · 404 agente/projeto inexistente.
 */
export function useMemoriaDoAgente(nome, projectId, { historico = false, enabled = true } = {}) {
  const api = useApi();
  const params = new URLSearchParams();
  if (projectId) params.set('project_id', projectId);
  params.set('historico', historico ? '1' : '0');
  return useQuery({
    queryKey: memoriaKeys.memoria(nome, projectId, historico),
    queryFn:  () => api.get(`/agents/${_nome(nome)}/memoria?${params.toString()}`),
    enabled:  Boolean(nome && projectId) && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * buscarAgenteNoCatalogo — procura um agente pelo nome exato (montagem inicial do squad).
 * Usa GET /agents?search=; devolve o AgentOut ou null. Função imperativa (fora de hook)
 * porque a montagem resolve vários nomes de uma vez.
 */
export async function buscarAgenteNoCatalogo(api, nome) {
  if (!nome) return null;
  try {
    const res = await api.get(`/agents?search=${encodeURIComponent(nome)}&limit=20`);
    const items = Array.isArray(res) ? res : (res?.items ?? []);
    return items.find((a) => a?.name === nome || a?.slug === nome) ?? null;
  } catch {
    return null;
  }
}
